import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import Tooltip from "./Tooltip";

type BubbleSelectorOption = {
  id: string;
  name: string;
  color?: string | null;
};

type BubbleSelectorProps = {
  selected: string | null | undefined;
  setSelected: (value: BubbleSelectorOption) => void;
  options: BubbleSelectorOption[];
  availableOptions?: BubbleSelectorOption[];
  disabledOptionExplanation?: string;
  className?: string;
  isRounded?: boolean;
  // Cap the number of swatches rendered, collapsing the rest into a "+N" chip.
  // Keeps the selector to a single fixed-height row on browse grids so cards
  // stay vertically aligned (a wrapped second row pushes the title down on one
  // card but not its neighbour). Omit to render every option (product page).
  maxVisible?: number;
  // Invoked when the "+N" overflow chip is clicked, in addition to opening the
  // popover of hidden colours. Optional — the popover is the primary affordance.
  onOverflowClick?: () => void;
};

function BubbleSelector({
  selected,
  setSelected,
  options,
  availableOptions,
  disabledOptionExplanation,
  className,
  isRounded = true,
  maxVisible,
  onOverflowClick,
}: BubbleSelectorProps) {
  const [overflowOpen, setOverflowOpen] = useState(false);
  const overflowRef = useRef<HTMLDivElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  // Viewport coords for the portalled popover. Null until measured — the
  // popover renders hidden for that one frame so it can't flash at 0,0.
  const [popoverPos, setPopoverPos] = useState<{
    top: number;
    left: number;
  } | null>(null);

  let visibleOptions =
    maxVisible != null ? options?.slice(0, maxVisible) : options;

  // If the colour picked from the overflow popover lives past the cap, swap it
  // into the last visible slot — otherwise the selection is invisible on the
  // card and the swatch row looks unresponsive to the click.
  const selectedIsHidden =
    selected != null &&
    maxVisible != null &&
    options?.slice(maxVisible).some((o) => o.id === selected);
  if (selectedIsHidden && visibleOptions?.length) {
    const selectedOption = options.find((o) => o.id === selected)!;
    visibleOptions = [...visibleOptions.slice(0, -1), selectedOption];
  }

  // Everything not currently on the row lives in the popover — derived from
  // the visible set rather than the raw cap so the colour displaced by the
  // swap above stays reachable (it used to vanish from both lists, and the
  // only way back to it was picking a different visible colour first).
  const visibleIds = new Set(visibleOptions?.map((o) => o.id));
  const hiddenOptions =
    maxVisible != null && options
      ? options.filter((o) => !visibleIds.has(o.id))
      : [];

  const overflowCount = hiddenOptions.length;

  // Anchor the portalled popover to the chip's viewport rect. Preferred
  // placement is above the chip (it clears the product name below); it flips
  // below when the top of the viewport is in the way, and clamps horizontally
  // so a right-aligned popover can't run off a narrow screen.
  const positionPopover = useCallback(() => {
    const chip = overflowRef.current;
    const pop = popoverRef.current;
    if (!chip || !pop) return;

    const c = chip.getBoundingClientRect();
    const p = pop.getBoundingClientRect();
    const GAP = 8; // breathing room between chip and popover
    const EDGE = 8; // min distance from the viewport edge

    let top = c.top - p.height - GAP;
    if (top < EDGE) {
      const below = c.bottom + GAP;
      top =
        below + p.height + EDGE <= window.innerHeight
          ? below
          : Math.max(EDGE, window.innerHeight - p.height - EDGE);
    }

    let left = c.right - p.width; // right-aligned to the chip
    left = Math.min(left, window.innerWidth - p.width - EDGE);
    left = Math.max(EDGE, left);

    setPopoverPos({ top, left });
  }, []);

  // Measure before paint, then keep the popover glued to the chip while the
  // page or the carousel row scrolls (capture:true catches scrolls on any
  // ancestor container, which don't bubble).
  useLayoutEffect(() => {
    if (!overflowOpen) {
      setPopoverPos(null);
      return;
    }
    positionPopover();
    window.addEventListener("scroll", positionPopover, true);
    window.addEventListener("resize", positionPopover);
    return () => {
      window.removeEventListener("scroll", positionPopover, true);
      window.removeEventListener("resize", positionPopover);
    };
  }, [overflowOpen, positionPopover]);

  // Close the popover on an outside click or Escape. The popover lives in a
  // portal, so "inside" means either the chip wrapper or the popover itself —
  // a DOM `contains` on the wrapper alone would treat every click on a colour
  // in the list as an outside click.
  useEffect(() => {
    if (!overflowOpen) return;

    const onPointerDown = (e: MouseEvent | TouchEvent) => {
      const target = e.target as Node;
      if (
        !overflowRef.current?.contains(target) &&
        !popoverRef.current?.contains(target)
      ) {
        setOverflowOpen(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOverflowOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("touchstart", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("touchstart", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [overflowOpen]);

  const isOptionDisabled = (option: BubbleSelectorOption) =>
    !!availableOptions &&
    !availableOptions.map((ao) => ao.id).includes(option.id);

  return (
    <div
      role="group"
      // When capped (browse grid), pin to a single non-wrapping row of fixed
      // height so the selected bubble growing to h-10 can't change the row
      // height and misalign neighbouring cards.
      className={`${isRounded ? "round-bubble-selector" : "square-bubble-selector"} ${
        maxVisible != null ? "!flex-nowrap h-10" : ""
      } ${className}`}
    >
      {visibleOptions?.map((option, idx) => {
        const isDisabled = isOptionDisabled(option);
        const isSelected = selected === option.id;

        return (
          <Tooltip
            key={idx}
            tooltip={disabledOptionExplanation ?? ""}
            position="top"
            disabled={!isDisabled}
          >
            <button
              type="button"
              style={{ backgroundColor: `#${option.color}` }}
              className={`${isRounded ? "round-bubble-selector-button" : "square-bubble-selector-button"} ${
                isSelected ? "selected w-10 h-10" : "w-8 h-8"
              } ${isDisabled && "disabled"}`}
              onClick={() => setSelected(option)}
              disabled={isDisabled}
              // Convey selection to assistive tech (a color swatch has no text).
              aria-pressed={isSelected}
              title={option.name}
              aria-label={
                isDisabled && disabledOptionExplanation
                  ? `${option.name} (${disabledOptionExplanation})`
                  : option.name
              }
            >
              {!option.color ? option.name : null}
            </button>
          </Tooltip>
        );
      })}
      {overflowCount > 0 && (
        <div className="relative inline-flex shrink-0" ref={overflowRef}>
          <button
            type="button"
            className={`${isRounded ? "round-bubble-selector-button" : "square-bubble-selector-button"} bubble-selector-overflow w-8 h-8`}
            onClick={(e) => {
              // The chip sits inside a clickable card — don't let the click
              // bubble up and navigate away from the popover we just opened.
              e.stopPropagation();
              setOverflowOpen((open) => !open);
              onOverflowClick?.();
            }}
            aria-haspopup="dialog"
            aria-expanded={overflowOpen}
            aria-label={`Show ${overflowCount} more colour${overflowCount === 1 ? "" : "s"}`}
            title={`${overflowCount} more colour${overflowCount === 1 ? "" : "s"}`}
          >
            +{overflowCount}
          </button>

          {overflowOpen &&
            createPortal(
              <div
                ref={popoverRef}
                role="dialog"
                aria-label="More colours"
                className="bubble-selector-overflow-popover"
                style={{
                  top: popoverPos?.top ?? 0,
                  left: popoverPos?.left ?? 0,
                  visibility: popoverPos ? undefined : "hidden",
                }}
                onClick={(e) => e.stopPropagation()}
              >
                <div className="flex flex-col gap-1">
                  {hiddenOptions.map((option) => {
                    const isDisabled = isOptionDisabled(option);
                    const isSelected = selected === option.id;

                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`flex items-center gap-2 rounded px-1.5 py-1 text-left text-xs normal-case ${
                          isDisabled
                            ? "cursor-not-allowed text-gray-400"
                            : "hover:bg-gray-100"
                        } ${isSelected ? "bg-gray-100 font-semibold" : ""}`}
                        onClick={() => {
                          setSelected(option);
                          setOverflowOpen(false);
                        }}
                        disabled={isDisabled}
                        aria-pressed={isSelected}
                        aria-label={
                          isDisabled && disabledOptionExplanation
                            ? `${option.name} (${disabledOptionExplanation})`
                            : option.name
                        }
                      >
                        <span
                          aria-hidden="true"
                          className="w-4 h-4 rounded-full border border-gray-300 shrink-0"
                          style={{
                            backgroundColor: option.color
                              ? `#${option.color}`
                              : "transparent",
                          }}
                        />
                        <span className="whitespace-nowrap">
                          {option.name}
                          {isDisabled && disabledOptionExplanation
                            ? ` — ${disabledOptionExplanation}`
                            : ""}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>,
              document.body,
            )}
        </div>
      )}
    </div>
  );
}

export default BubbleSelector;
